import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import type { SavedDepartment } from "@mtform/shared";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DepartmentCombobox } from "@/components/form/DepartmentCombobox";
import { SavedValuePicker } from "@/components/form/SavedValuePicker";
import { useMetaDepartments } from "@/hooks/useMetaDirectory";
import { WizardCard } from "../WizardCard";
import { usePatientLookupState } from "../PatientLookupContext";
import type { TripFormValues } from "../schema";
import type { WizardStepStatus } from "../WizardCard";

interface PatientDetailsStepProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

// =============================================================================
// PatientDetailsStep
//
// Department + cost center, plus first/last name when we don't already
// have them. For returning patients the name was confirmed on the
// previous (lookup) card, so re-asking is busywork — we hide those
// fields whenever both are populated. New patients (or returning ones
// where the directory had a blank name) still see the inputs.
//
// Department + cost center stay visible even for returning patients
// because they can vary booking-to-booking (the same patient might be
// booked under Cardiology one week, Oncology the next).
// =============================================================================

export function PatientDetailsStep({
  index,
  status,
  onContinue,
  onEdit,
}: PatientDetailsStepProps) {
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const lookup = usePatientLookupState();
  const departmentWatch = useWatch({ control, name: "department" }) ?? "";
  const costCenterWatch = useWatch({ control, name: "costCenter" }) ?? "";
  const firstNameWatch = useWatch({ control, name: "firstName" }) ?? "";
  const lastNameWatch = useWatch({ control, name: "lastName" }) ?? "";

  // Reverse lookup: cost center → department name. Mount Sinai's admin
  // team wants typing a cost center to auto-fill the canonical
  // department name (uniformity — everyone booking under cost center
  // 1234 gets the exact same "Cardiology" spelling rather than a dozen
  // variations). Built from the same admin-managed department
  // directory the Department typeahead uses, so the two stay in sync.
  //
  // Cost centers that aren't in the directory simply don't fill
  // anything — which surfaces them in the Admin → Departments
  // "Unmatched" review queue, exactly the "capture cost centers we
  // haven't entered yet" outcome Zakia asked for.
  const { data: metaDepartments } = useMetaDepartments();
  const costCenterToDepartment = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of metaDepartments?.departments ?? []) {
      const cc = (d.defaultCostCenter ?? "").trim();
      // First match wins — if two departments somehow share a cost
      // center, the directory order (sortOrder, then name) decides.
      if (cc && !map.has(cc)) map.set(cc, d.name);
    }
    return map;
  }, [metaDepartments]);

  const fillDepartmentFromCostCenter = useCallback(
    (rawCostCenter: string) => {
      const match = costCenterToDepartment.get(rawCostCenter.trim());
      if (!match) return;
      // Only write when the resolved name actually differs — avoids
      // marking the field dirty / re-rendering on every keystroke once
      // it's already correct.
      if (normalize(match) !== normalize(getValues("department"))) {
        setValue("department", match, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [costCenterToDepartment, getValues, setValue],
  );

  // Keep RHF's onChange for the cost-center field but layer the
  // department auto-fill on top. register() returns a stable handler;
  // we capture it once and call it inside our wrapper.
  const costCenterField = register("costCenter");

  // Anchor the "show name inputs?" decision on the patient *lookup*
  // result, not on the live form values. Driving this off the
  // first/last RHF watches meant any moment where both fields
  // happened to be non-empty would collapse the section — including
  // mid-keystroke autofills (the browser / 1Password setting last
  // name immediately after the user typed first name and tabbed
  // over), which unmounts the input the user is actively editing.
  //
  // The directory either knows the patient's name or it doesn't, and
  // that doesn't change while the admin is typing into Department.
  //
  // * New patient  (status "not-found") → always show inputs.
  // * Returning patient with a name on file → hide inputs (no busy-
  //   work; the name was already confirmed on the lookup card).
  // * Returning patient where the directory row has a blank first or
  //   last name (rare, but possible for TMS-synced rows) → show
  //   inputs so the admin can fill them in.
  const lookedUpPatient = lookup.status === "found" ? lookup.patient : null;
  const directoryHasFullName = Boolean(
    (lookedUpPatient?.firstName ?? "").trim() &&
      (lookedUpPatient?.lastName ?? "").trim(),
  );
  const showNameInputs = !directoryHasFullName;

  const allSavedDepartments =
    lookup.status === "found" && lookup.patient ? lookup.patient.savedDepartments : [];
  const savedDepartments = useMemo(
    () =>
      allSavedDepartments.filter(
        (s) =>
          normalize(s.department) !== normalize(departmentWatch) ||
          normalize(s.costCenter) !== normalize(costCenterWatch),
      ),
    [allSavedDepartments, departmentWatch, costCenterWatch],
  );

  const renderSavedDepartment = useCallback(
    (s: SavedDepartment) => ({
      primary: s.department || "(no department)",
      meta: s.costCenter ? `Cost center ${s.costCenter}` : undefined,
    }),
    [],
  );

  const onSelectSavedDepartment = useCallback(
    (s: SavedDepartment) => {
      setValue("department", s.department, { shouldDirty: true, shouldTouch: true });
      if (s.costCenter) {
        setValue("costCenter", s.costCenter, { shouldDirty: true, shouldTouch: true });
      }
    },
    [setValue],
  );

  async function handleContinue() {
    const fields: Array<keyof TripFormValues> = ["costCenter"];
    if (showNameInputs) {
      fields.push("firstName", "lastName");
    }
    const ok = await trigger(fields);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const fullName = `${firstNameWatch} ${lastNameWatch}`.trim();
    const segs: string[] = [];
    if (fullName) segs.push(fullName);
    if (departmentWatch) segs.push(departmentWatch);
    if (costCenterWatch) segs.push(`CC ${costCenterWatch}`);
    return (
      <WizardCard
        index={index}
        title="Patient details"
        status="complete"
        summary={segs.length > 0 ? segs.join(" · ") : "—"}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Patient details"
      description={
        showNameInputs
          ? "Fill in the patient's name, department, and cost center."
          : "Department and cost center can vary per booking — verify or update them."
      }
      status={status}
    >
      {showNameInputs ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="First name" required htmlFor="firstName" error={errors.firstName?.message}>
            <Input
              id="firstName"
              autoComplete="off"
              autoFocus
              maxLength={50}
              clearable
              {...register("firstName")}
            />
          </Field>
          <Field label="Last name" required htmlFor="lastName" error={errors.lastName?.message}>
            <Input
              id="lastName"
              autoComplete="off"
              maxLength={50}
              clearable
              {...register("lastName")}
            />
          </Field>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Field label="Department" htmlFor="department">
            <Controller
              control={control}
              name="department"
              render={({ field }) => (
                <DepartmentCombobox
                  id="department"
                  autoComplete="organization"
                  maxLength={100}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onMatch={(d) => {
                    const currentCC = String(getValues("costCenter") ?? "").trim();
                    if (d.defaultCostCenter && !currentCC) {
                      setValue("costCenter", d.defaultCostCenter, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }
                  }}
                />
              )}
            />
          </Field>
          {savedDepartments.length > 0 ? (
            <SavedValuePicker
              label="Saved"
              items={savedDepartments}
              renderItem={renderSavedDepartment}
              onSelect={onSelectSavedDepartment}
              description="Departments this patient has been booked under before."
            />
          ) : null}
        </div>
        <Field
          label="Cost center #"
          required
          htmlFor="costCenter"
          error={errors.costCenter?.message}
        >
          <Input
            id="costCenter"
            autoComplete="off"
            inputMode="numeric"
            maxLength={20}
            clearable
            {...costCenterField}
            onChange={(e) => {
              // Keep React Hook Form's value in sync first, then run
              // the cost-center → department auto-fill.
              void costCenterField.onChange(e);
              fillDepartmentFromCostCenter(e.target.value);
            }}
          />
        </Field>
      </div>

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={handleContinue} className="min-w-[10rem]">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}

function normalize(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}
